import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { QuoteCTA } from "@/components/QuoteCTA";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ArrowRight, Hammer, Wrench, Scissors, Flame, Truck, Factory } from "lucide-react";
import hero from "@/assets/awning-frame-fabric-patterning-shop.jpg";
import shop from "@/assets/400-s-main-dome-awning-shop.jpg";

export const Route = createFileRoute("/built-in-texas")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Built In Texas — Custom Awning Fabrication Shop | San Antonio & Austin",
      description:
        "Welded in Texas. We design, weld, sew, powder-coat & install custom awnings, sail shades & motorized patio shades across San Antonio, Austin & the Hill Country. 30+ yrs.",
      image: hero,
      imageAlt: "Texas Made Shade fabrication shop serving San Antonio & Austin",
      keywords:
        "custom awning fabrication Texas, made in Texas awnings, awning company San Antonio, Austin awning company, custom welded awnings San Antonio, San Antonio shade fabrication, Austin custom awnings, motorized patio shades Austin, motorized shades San Antonio, smart shades Austin TX, automatic patio shade, Texas awning manufacturer, in-house welding and sewing",
    }),
    scripts: serviceHeadScripts({
      path: "/built-in-texas",
      serviceName: "Custom Texas Awning Fabrication",
      breadcrumbName: "Built In Texas",
      description:
        "Texas Made Shade is a full custom fabrication shop in Seguin, TX. In-house welding, sewing, CNC, powder coating and installation — not a sales-and-install reseller.",
      faqs: [
        { q: "Are you a fabricator or just an installer?", a: "Both. We design, weld, sew, finish and install in-house. Most of our competitors install pre-built systems from a catalog — we build from raw aluminum, steel and Sunbrella® canvas in our Seguin, TX shop." },
        { q: "What can you custom-build?", a: "Welded aluminum and steel awning frames, tension shade structures, sail-shade posts, dog boxes, hog traps, deer blinds, trailer covers, drop curtains, hunting tarps, boat covers, custom storefront signage frames and one-off engineering jobs." },
        { q: "Do you ship outside Texas?", a: "We install across Texas. For self-install or freight-out projects, send photos and dimensions — we'll let you know if we can build and crate for shipping." },
        { q: "How long have you been in business?", a: "Nearly 30 years. Same family, same shop, same hands-on build." },
      ],
    }),
  }),
  component: BuiltInTexasPage,
});

const CAPS = [
  { icon: <Flame className="h-6 w-6" />, title: "In-House Welding", text: "Aluminum and steel MIG/TIG welding. Custom awning frames, sail-shade posts, dog boxes, trailers and one-off structural fabrication." },
  { icon: <Scissors className="h-6 w-6" />, title: "In-House Sewing", text: "Industrial sewing for Sunbrella®, Tempotest®, Dickson®, vinyl and mesh. Tops, covers, valances, drop curtains — patterned and stitched on site." },
  { icon: <Hammer className="h-6 w-6" />, title: "Custom Design", text: "We pattern, prototype and engineer custom shade structures — not just resell catalog parts. 30 years of field problem-solving." },
  { icon: <Wrench className="h-6 w-6" />, title: "Powder Coating & Finish", text: "Frames finished to your color spec — white, bronze, black, sandstone or matched to your building." },
  { icon: <Truck className="h-6 w-6" />, title: "Statewide Installation", text: "Our crews install across San Antonio, Austin, the Hill Country, South Texas and the Gulf Coast — every install backed by our own warranty." },
  { icon: <Factory className="h-6 w-6" />, title: "Authorized Dealer", text: "Certified Solair® retractable awning dealer. Premium Sunbrella®, Tempotest®, Phifertex®, Commercial 95 and Somfy® components." },
];

function BuiltInTexasPage() {
  return (
    <div className="min-h-screen bg-cream text-navy-deep">
      <SiteHeader />
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <img src={hero} alt="Texas Made Shade fabricator patterning an awning frame in the Seguin shop" className="absolute inset-0 h-full w-full object-cover opacity-30" loading="eager" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 lg:px-8 lg:py-28">
          <Breadcrumbs items={[{ label: "Built In Texas" }]} className="text-white/80" />
          <div className="mt-6 font-display text-xs uppercase tracking-[0.3em] text-red">★ BUILT IN TEXAS ★</div>
          <h1 className="mt-3 max-w-4xl font-display text-4xl font-bold uppercase leading-tight md:text-6xl">
            Built In Texas. Engineered For Texas. Fabricated By Texans.
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-white/85 md:text-xl">
            We're not just another awning installer. Texas Made Shade is a full custom fabrication shop — we design, weld, sew, finish and install custom awnings, sail shades and motorized patio shades for homes and businesses across <strong>San Antonio</strong>, <strong>Austin</strong> and the Hill Country. 30 years. Same family. Same Texas hands.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/estimate-quotes" className="inline-flex items-center gap-2 bg-red px-6 py-3 font-display text-sm font-extrabold uppercase tracking-wider text-white shadow-lg ring-1 ring-red-deep/40 transition hover:bg-red-deep">
              Get a Custom Quote <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/why-texas-made-shade" className="inline-flex items-center gap-2 border-2 border-white/70 bg-white/10 px-6 py-3 font-display text-sm font-extrabold uppercase tracking-wider text-white transition hover:bg-white hover:text-navy-deep">
              Why Texas Made Shade
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-cream py-20">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ OUR CAPABILITIES ★</div>
          <h2 className="mt-2 font-display text-3xl font-bold uppercase leading-tight md:text-4xl">A Real Shop — Not A Catalog</h2>
          <p className="mt-3 max-w-3xl text-base text-navy-deep/75">
            We design, weld, sew, recover, repair and install custom shade solutions right here in Texas. Below is what happens inside our Seguin shop on any given day.
          </p>
          <ul className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {CAPS.map((c) => (
              <li key={c.title} className="flex h-full flex-col border-2 border-navy-deep/15 bg-white p-6 transition hover:border-red">
                <div className="flex items-center gap-3 text-red">{c.icon}<h3 className="font-display text-lg font-bold uppercase tracking-wide text-navy-deep">{c.title}</h3></div>
                <p className="mt-3 text-sm text-navy-deep/80">{c.text}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-t-8 border-navy-deep bg-white py-20">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          <div className="grid items-center gap-10 md:grid-cols-2">
            <div>
              <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ THE SHOP ★</div>
              <h2 className="mt-2 font-display text-3xl font-bold uppercase md:text-4xl">A Real Address. A Real Shop.</h2>
              <p className="mt-4 text-navy-deep/80">
                Most shade companies you find online are marketing companies with a phone bank. We're a fabrication shop with a street address in Seguin, TX. You can drive over, see the frames being welded, watch the canvas being patterned and meet the crew who'll install your job.
              </p>
              <p className="mt-3 text-navy-deep/80">
                That matters when something needs adjusting in year 7. You call us — not a 1-800 number — and we'll show up.
              </p>
              <Link to="/contact" className="mt-6 inline-flex items-center gap-2 bg-red px-5 py-3 font-display text-xs font-extrabold uppercase tracking-wider text-white hover:bg-red-deep">
                Visit Our Shop <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            <img src={shop} alt="Texas Made Shade fabrication shop, 400 S Main, Seguin TX" className="h-full w-full object-cover" loading="lazy" />
          </div>
        </div>
      </section>

      {/* By the numbers */}
      <section className="bg-cream py-16">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ BY THE NUMBERS ★</div>
          <h2 className="mt-2 font-display text-3xl font-bold uppercase md:text-4xl">30 Years. Two Major Texas Markets. One Shop.</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-4 text-center">
            {[
              { n: "30+", l: "Years building shade in Texas" },
              { n: "2", l: "Anchor markets — San Antonio & Austin" },
              { n: "80+", l: "Texas cities we install in" },
              { n: "100%", l: "Welded, sewn & installed in-house" },
            ].map((s) => (
              <div key={s.l} className="border-2 border-navy-deep/15 bg-white p-6">
                <div className="font-display text-4xl font-extrabold text-red">{s.n}</div>
                <div className="mt-2 text-xs uppercase tracking-wide text-navy-deep/70">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our story timeline */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-4xl px-4 lg:px-8">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ OUR STORY ★</div>
          <h2 className="mt-2 font-display text-3xl font-bold uppercase md:text-4xl">From One Sewing Machine To San Antonio & Austin's Custom Awning Shop</h2>
          <ol className="mt-8 space-y-6 border-l-4 border-red pl-6">
            <li>
              <div className="font-display text-sm uppercase tracking-[0.2em] text-red">The 1990s — One sewing machine</div>
              <p className="mt-1 text-navy-deep/80">Started with a single industrial sewing machine and one truck — patterning canvas covers for ranchers and small businesses across South Texas.</p>
            </li>
            <li>
              <div className="font-display text-sm uppercase tracking-[0.2em] text-red">The 2000s — Welding shop added</div>
              <p className="mt-1 text-navy-deep/80">Brought welding in-house so frames, posts and brackets could be fabricated alongside the canvas — one shop, one warranty, no subcontractors.</p>
            </li>
            <li>
              <div className="font-display text-sm uppercase tracking-[0.2em] text-red">The 2010s — Authorized Solair® dealer</div>
              <p className="mt-1 text-navy-deep/80">Became a certified Solair® retractable awning dealer and expanded into motorized patio shades for San Antonio and Austin homeowners.</p>
            </li>
            <li>
              <div className="font-display text-sm uppercase tracking-[0.2em] text-red">Today — Fabricator + dealer + installer</div>
              <p className="mt-1 text-navy-deep/80">We're the rare shop that's all three: authorized dealer for premium brands, custom fabricator for one-offs, and the crew that actually installs and services every job across San Antonio, Austin and the Hill Country.</p>
            </li>
          </ol>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/san-antonio" className="inline-flex items-center gap-2 bg-red px-5 py-3 font-display text-xs font-extrabold uppercase tracking-wider text-white hover:bg-red-deep">San Antonio Service Area <ArrowRight className="h-3 w-3" /></Link>
            <Link to="/austin" className="inline-flex items-center gap-2 bg-navy-deep px-5 py-3 font-display text-xs font-extrabold uppercase tracking-wider text-white hover:opacity-90">Austin Service Area <ArrowRight className="h-3 w-3" /></Link>
          </div>
        </div>
      </section>
      <QuoteCTA title="Need Something Custom Built In Texas?" subtitle="Serving San Antonio, Austin and the Hill Country. Send photos and dimensions — we'll design, fabricate and install it." />
      <SiteFooter />
    </div>
  );
}
